
import { createSlice } from '@reduxjs/toolkit';
import { TicketingState, Invoice } from './types';
// 👇 Add this import (adjust path if your tsconfig baseUrl/paths differ)
import type { RootState } from '@/redux/store'; // adjust to your store path

import {
  createTicket,
  updateTicketData,
  addTicketItem,
  deleteTicketItem,
  uploadTicketDocument,
  completeTicket,
  submitAddedItems,
} from './thunk';

const initialState: TicketingState = {
  currentTicket: null,

  // create
  isCreating: false,
  createError: null,

  // update data
  isUpdating: false,
  updateError: null,
  lastUpdateMessage: null,

  // add item
  isAddingItem: false,
  addItemError: null,
  lastAddItemMessage: null,

  // delete item
  isDeletingItem: false,
  deleteItemError: null,
  lastDeleteMessage: null,

  // upload doc
  isUploadingDoc: false,
  uploadDocError: null,
  lastUploadMessage: null,

  // complete
  isCompleting: false,
  completeError: null,
  lastCompleteMessage: null,

  // invoice
  lastInvoice: null,

  // submit added items
  isSubmittingItems: false,
  submitItemsError: null,
  lastSubmitItemsMessage: null,
};

export const ticketingSlice = createSlice({
  name: 'ticketing',
  initialState,
  reducers: {
    resetTicketing: () => initialState,
    setCurrentTicket(state, action: { payload: string }) {
      state.currentTicket = action.payload;
    },
    clearTicketErrors(state) {
      state.createError = null;
      state.updateError = null;
      state.addItemError = null;
      state.deleteItemError = null;
      state.uploadDocError = null;
      state.completeError = null;
      state.submitItemsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /** Create Ticket */
      .addCase(createTicket.pending, (state) => {
        state.isCreating = true;
        state.createError = null;
      })
      .addCase(createTicket.fulfilled, (state, action) => {
        state.isCreating = false;
        state.currentTicket = action.payload.ticket ?? null;
      })
      .addCase(createTicket.rejected, (state, action) => {
        state.isCreating = false;
        state.createError = typeof action.payload === 'string' && action.payload ? action.payload : 'Failed to create ticket';
      })

      /** Update Ticket Data */
      .addCase(updateTicketData.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
        state.lastUpdateMessage = null;
      })
      .addCase(updateTicketData.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.lastUpdateMessage = action.payload?.message ?? 'Updated.';
      })
      .addCase(updateTicketData.rejected, (state, action) => {
        state.isUpdating = false;
        state.updateError = typeof action.payload === 'string' && action.payload ? action.payload : 'Failed to update ticket';
      })

      /** Add Item */
      .addCase(addTicketItem.pending, (state) => {
        state.isAddingItem = true;
        state.addItemError = null;
        state.lastAddItemMessage = null;
      })
      .addCase(addTicketItem.fulfilled, (state, action) => {
        state.isAddingItem = false;
        state.lastAddItemMessage = action.payload?.message ?? 'Item added.';
      })
      .addCase(addTicketItem.rejected, (state, action) => {
        state.isAddingItem = false;
        state.addItemError = typeof action.payload === 'string' && action.payload ? action.payload : 'Failed to add item';
      })

      /** Delete Item */
      .addCase(deleteTicketItem.pending, (state) => {
        state.isDeletingItem = true;
        state.deleteItemError = null;
        state.lastDeleteMessage = null;
      })
      .addCase(deleteTicketItem.fulfilled, (state, action) => {
        state.isDeletingItem = false;
        state.lastDeleteMessage = action.payload?.message ?? 'Item deleted.';
      })
      .addCase(deleteTicketItem.rejected, (state, action) => {
        state.isDeletingItem = false;
        state.deleteItemError = typeof action.payload === 'string' && action.payload ? action.payload : 'Failed to delete item';
      })

      /** Upload Document */
      .addCase(uploadTicketDocument.pending, (state) => {
        state.isUploadingDoc = true;
        state.uploadDocError = null;
        state.lastUploadMessage = null;
      })
      .addCase(uploadTicketDocument.fulfilled, (state, action) => {
        state.isUploadingDoc = false;
        state.lastUploadMessage = action.payload?.message ?? 'Uploaded.';
      })
      .addCase(uploadTicketDocument.rejected, (state, action) => {
        state.isUploadingDoc = false;
        state.uploadDocError = typeof action.payload === 'string' && action.payload ? action.payload : 'Failed to upload document';
      })

      /** Complete Ticket */
      .addCase(completeTicket.pending, (state) => {
        state.isCompleting = true;
        state.completeError = null;
        state.lastCompleteMessage = null;
        state.lastInvoice = null;
      })
      .addCase(completeTicket.fulfilled, (state, action) => {
        state.isCompleting = false;
        state.lastCompleteMessage = action.payload?.message ?? 'Completed.';
        state.lastInvoice = (action.payload?.invoice ?? null) as Invoice | null;
      })
      .addCase(completeTicket.rejected, (state, action) => {
        state.isCompleting = false;
        state.completeError = typeof action.payload === 'string' && action.payload ? action.payload : 'Failed to complete ticket';
      })

      /** Submit Added Items (batch) */
      .addCase(submitAddedItems.pending, (state) => {
        state.isSubmittingItems = true;
        state.submitItemsError = null;
        state.lastSubmitItemsMessage = null;
      })
      .addCase(submitAddedItems.fulfilled, (state, action) => {
        state.isSubmittingItems = false;
        state.lastSubmitItemsMessage =
          action.payload?.message ?? 'Items submitted.';
      })
      .addCase(submitAddedItems.rejected, (state, action) => {
        state.isSubmittingItems = false;
        state.submitItemsError = typeof action.payload === 'string' && action.payload ? action.payload : 'Failed to submit items';
      });
  },
});

export const { resetTicketing, setCurrentTicket, clearTicketErrors } =
  ticketingSlice.actions;
export default ticketingSlice.reducer;

/** =========================
 * Selectors (typed with RootState)
 * ========================= */

type RS = RootState;

/** Whole slice */
export const selectTicketing = (s: RS) => s.ticketing;

/** Core data */
export const selectTicketId = (s: RS) => s.ticketing.currentTicket;
export const selectLastInvoice = (s: RS) => s.ticketing.lastInvoice;

/** Loading flags */
export const selectIsCreating = (s: RS) => s.ticketing.isCreating;
export const selectIsUpdating = (s: RS) => s.ticketing.isUpdating;
export const selectIsAddingItem = (s: RS) => s.ticketing.isAddingItem;
export const selectIsDeletingItem = (s: RS) => s.ticketing.isDeletingItem;
export const selectIsUploadingDoc = (s: RS) => s.ticketing.isUploadingDoc;
export const selectIsCompleting = (s: RS) => s.ticketing.isCompleting;
export const selectIsSubmittingItems = (s: RS) => s.ticketing.isSubmittingItems;

/** Error flags */
export const selectCreateError = (s: RS) => s.ticketing.createError;
export const selectUpdateError = (s: RS) => s.ticketing.updateError;
export const selectAddItemError = (s: RS) => s.ticketing.addItemError;
export const selectDeleteItemError = (s: RS) => s.ticketing.deleteItemError;
export const selectUploadDocError = (s: RS) => s.ticketing.uploadDocError;
export const selectCompleteError = (s: RS) => s.ticketing.completeError;
export const selectSubmitItemsError = (s: RS) => s.ticketing.submitItemsError;

/** Last messages */
export const selectLastUpdateMessage = (s: RS) => s.ticketing.lastUpdateMessage;
export const selectLastAddItemMessage = (s: RS) =>
  s.ticketing.lastAddItemMessage;
export const selectLastDeleteMessage = (s: RS) => s.ticketing.lastDeleteMessage;
export const selectLastUploadMessage = (s: RS) => s.ticketing.lastUploadMessage;
export const selectLastCompleteMessage = (s: RS) =>
  s.ticketing.lastCompleteMessage;
export const selectLastSubmitItemsMessage = (s: RS) =>
  s.ticketing.lastSubmitItemsMessage;

/** Rollups */
export const selectAnyLoading = (s: RS) =>
  s.ticketing.isCreating ||
  s.ticketing.isUpdating ||
  s.ticketing.isAddingItem ||
  s.ticketing.isDeletingItem ||
  s.ticketing.isUploadingDoc ||
  s.ticketing.isCompleting ||
  s.ticketing.isSubmittingItems;

export const selectAnyError = (s: RS) =>
  s.ticketing.createError ||
  s.ticketing.updateError ||
  s.ticketing.addItemError ||
  s.ticketing.deleteItemError ||
  s.ticketing.uploadDocError ||
  s.ticketing.completeError ||
  s.ticketing.submitItemsError;
